import { memo, useCallback, useState } from 'react'

import type { ProductImageKind } from '../../models/productImage'

import { PRODUCT_IMAGE_FALLBACKS } from './fallbackImages'

interface Props {
  /** Catalogue URL. Absent, empty or broken all land on the fallback. */
  readonly src?: string | null
  readonly alt: string
  readonly kind: ProductImageKind
  readonly className?: string
}

/**
 * A product image that falls back to a drawing of its kind.
 *
 * Two ways the catalogue image goes missing and both end in the same place: the
 * search answered without a URL (mapped to `''` upstream), or the URL was there
 * and the browser could not load it. What is remembered is the URL that failed,
 * not a flag — a virtualised card reused for another product then retries
 * instead of staying stuck on the fallback.
 */
export const ProductImage = memo<Props>(({ src, alt, kind, className }) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const handleError = useCallback(() => setFailedSrc(src ?? null), [src])

  if (src && failedSrc !== src) {
    return <img className={className} src={src} alt={alt} loading="lazy" onError={handleError} />
  }

  const Fallback = PRODUCT_IMAGE_FALLBACKS[kind]

  return (
    <Fallback
      className={className}
      data-fallback={kind}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      focusable="false"
    />
  )
})

ProductImage.displayName = 'ProductImage'
